import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { CircleNotch, Check, CaretDown } from "phosphor-react";
import { ReactComponent as MemberLogo } from "assets/images/member.svg";
import { ReactComponent as QuestionLogo } from "assets/images/question.svg";
import DateInput from "components/common/date-picker";
import { getEcmStatus, getSurveyStaff } from "containers/dashboard/api";

const options = ["Last 7 Days", "Last 10 Days", "Last 20 Days"];

function SurveyStats({
  surveyStats,
  startDate,
  endDate,
  handleStartDate,
  handleEndDate,
}) {
  const {
    totalQuestions,
    completedSurveys,
    pendingSurveys,
    inProgressSurveys,
    activeMembersCount,
  } = surveyStats;

  const [ecmSelectedOption, setEcmSelectedOption] = useState("Last 7 Days");
  const [surveyStaffSelectedOption, setSurveyStaffSelectedOption] = useState(
    "Last 7 Days"
  );
  const [ecmIsOpen, setEcmIsOpen] = useState(false);
  const [surveyStaffIsOpen, setSurveyStaffIsOpen] = useState(false);
  const [ecmData, setEcmData] = useState([]);
  const [surveyStaffData, setSurveyStaffData] = useState([]);

  const getDaysFromOption = (option) => {
    switch (option) {
      case "Last 7 Days":
        return 7;
      case "Last 10 Days":
        return 10;
      case "Last 20 Days":
        return 20;
      default:
        return 7;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "-") return "-";
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${month}/${day}`;
  };

  const fetchEcmData = async (days) => {
    try {
      const result = await getEcmStatus(days);
      const formattedData = result.map((item) => ({
        day: formatDate(item.day) || "-",
        Enrolled: item?.Enrolled ?? 0,
        Outreach: item?.Outreach ?? 0,
        Excluded: item?.Excluded ?? 0,
      }));
      setEcmData(formattedData);
    } catch (err) {
      console.error("ECM data error:", err);
    }
  };

  const fetchSurveyStaffData = async (days) => {
    try {
      const result = await getSurveyStaff(days);
      const formattedData = result.map((item) => ({
        staff_name: item?.staff_name || "-",
        survey_count: item?.survey_count ?? 0,
      }));
      setSurveyStaffData(formattedData);
    } catch (err) {
      console.error("Survey Staff data error:", err);
    }
  };

  useEffect(() => {
    const days = getDaysFromOption(ecmSelectedOption);
    fetchEcmData(days);
  }, [ecmSelectedOption]);

  useEffect(() => {
    const days = getDaysFromOption(surveyStaffSelectedOption);
    fetchSurveyStaffData(days);
  }, [surveyStaffSelectedOption]);

  return (
    <div className="row">
      {[
        {
          label: "Active MIF Members",
          value: activeMembersCount,
          Icon: MemberLogo,
        },
        { label: "Surveys completed", value: completedSurveys, Icon: Check },
        {
          label: "Surveys in progress",
          value: inProgressSurveys,
          Icon: CircleNotch,
        },

        // { label: "Total Questions", value: totalQuestions, Icon: QuestionLogo },
      ].map(({ label, value, Icon }, i) => (
        <div
          key={i}
          className="col-6 col-md-6 col-lg-6 col-xl-6 col-xxl-3 mb-3 state-card-container"
        >
          <div className="state-card">
            <div className="d-flex flex-column">
              <p className="opacity-50">{label}</p>
              <div className="d-flex justify-content-between align-items-center flex-row">
                <p className="f-w">{value}</p>
                <Icon size={40} className="clr" />
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Charts */}
      <div className="container-fluid p-2.5 mt-3">
        <div className="row g-4">
          {/* Chart 1 */}
          <div className="col-12 col-lg-6">
            <div className="bg-white rounded-3 chart-p h-100">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <p className="fw-bold mb-0 dc-h">No. Surveys Submitted</p>
                <div
                  className="custom-select"
                  onClick={() => setEcmIsOpen(!ecmIsOpen)}
                >
                  <span>{ecmSelectedOption}</span>
                  <span
                    className="chart-caret"
                    style={{
                      transform: ecmIsOpen
                        ? "rotate(180deg) translateY(-12%)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease-in-out",
                    }}
                  >
                    <CaretDown weight="bold" />
                  </span>
                  {ecmIsOpen && (
                    <ul className="chart-dropdown">
                      {options.map((option, index) => (
                        <li
                          className="chart-dropdown-list"
                          key={index}
                          onClick={() => {
                            setEcmSelectedOption(option);
                            setEcmIsOpen(false);
                          }}
                        >
                          {option}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div style={{ height: "360px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ecmData}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tickMargin={15}
                    />
                    <YAxis
                      width={30}
                      tickCount={7}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Bar
                      dataKey="Enrolled"
                      stackId="1"
                      fill="#22C55E"
                      barSize={21.28}
                    >
                      {ecmData.map((el, index) =>
                        el.Outreach === 0 && el.Excluded === 0 ? (
                          <Cell radius={[40, 40, 0, 0]} key={index} />
                        ) : (
                          <Cell radius={[0, 0, 0, 0]} key={index} />
                        )
                      )}
                    </Bar>
                    <Bar
                      dataKey="Outreach"
                      stackId="1"
                      fill="#EAB308"
                      barSize={21.28}
                    >
                      {ecmData.map((el, index) =>
                        el.Excluded === 0 ? (
                          <Cell radius={[40, 40, 0, 0]} key={index} />
                        ) : (
                          <Cell radius={[0, 0, 0, 0]} key={index} />
                        )
                      )}
                    </Bar>
                    <Bar
                      dataKey="Excluded"
                      stackId="1"
                      fill="#EF4444"
                      barSize={21.28}
                      radius={[40, 40, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="d-flex gap-5">
                <p className="enrolled">Enrolled</p>
                <p className="pendings">Outreach</p>
                <p className="excluded">Excluded</p>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="bg-white rounded-3 p-4 h-100">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <p className="fw-bold mb-0 dc-h">No. of Surveys Completed</p>
                <div
                  className="custom-select"
                  onClick={() => setSurveyStaffIsOpen(!surveyStaffIsOpen)}
                >
                  <span>{surveyStaffSelectedOption}</span>
                  <span
                    className="chart-caret"
                    style={{
                      transform: surveyStaffIsOpen
                        ? "rotate(180deg) translateY(-12%)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease-in-out",
                    }}
                  >
                    <CaretDown weight="bold" />
                  </span>
                  {surveyStaffIsOpen && (
                    <ul className="chart-dropdown">
                      {options.map((option, index) => (
                        <li
                          className="chart-dropdown-list"
                          key={index}
                          onClick={() => {
                            setSurveyStaffSelectedOption(option);
                            setSurveyStaffIsOpen(false);
                          }}
                        >
                          {option}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div style={{ height: "360px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={surveyStaffData}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="staff_name"
                      axisLine={false}
                      tickLine={false}
                      tickMargin={15}
                    />
                    <YAxis
                      width={30}
                      tickCount={7}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Bar
                      dataKey="survey_count"
                      stackId="1"
                      fill="#22C55E"
                      barSize={21.28}
                      radius={[40, 40, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SurveyStats;
